/* import __COLOCATED_TEMPLATE__ from './signup-location-input.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dropTask, task, waitForProperty } from 'ember-concurrency';
import { BehaviorSubject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
} from 'rxjs/operators';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import clsx from 'clsx';

const CATEGORIES = {
  CITY: 'city',
  COUNTRY: 'country',
  REGION: 'region',
};

const CLASS_NAME = 'SH-c-signup-location-input-container';

const GOOGLE_PLACE_AUTOCOMPLETE_OPTIONS = {
  types: ['(cities)'],
};

const GOOGLE_PLACE_DETAILS_OPTIONS = {
  fields: ['place_id', 'formatted_address'],
};

const MAX_RESULT_COUNT = 9;

const SEARCH_DEBOUNCE_TIME_MS = 250;

const handleAPIResponse = (_, status) => {
  if (
    status != google.maps.places.PlacesServiceStatus.OK
    && status != google.maps.places.PlacesServiceStatus.ZERO_RESULTS
  ) {
    throw new Error(`Cannot get predictions. Status: ${status}`);
  }
};

class SignupLocationInput extends Component {
  @service('google-places') googlePlaces;
  @service('commands/location-commands') locationCommandService;
  @service store;

  @tracked googleAutocompleteService = null;
  @tracked googlePlacesService = null;
  @tracked hasFocus = false;
  @tracked isDirty = false;

  @tracked cities = [];
  @tracked countries = [];
  @tracked regions = [];

  #initialized = false;
  #googleAutocompleteSessionToken = null;
  #valueSubscription = null;

  willDestroy() {
    super.willDestroy();

    this.#valueSubscription?.unsubscribe();
  }

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get value() {
    return this.args.value?.toString() ?? '';
  }

  get showOptions() {
    if (!this.value) return false;
    if (!this.hasFocus) return false;
    if (!this.isDirty) return false;

    // show dropdown if there are results or resolved without results
    return true;
  }

  get isLoadingOptions() {
    return this.loadCities.isRunning
      || this.loadCitiesAPI.isRunning
      || this.handleOptionClick.isRunning
      || this.loadCountries.isRunning
      || this.loadRegions.isRunning;
  }

  get enabledCategoryCount() {
    const { disableCountries, disableRegions, disableCities } = this.args;
    return [disableCountries, disableRegions, disableCities].reduce((count, categoryDisabled) => {
      return !categoryDisabled ? count + 1 : count;
    }, 0);
  }

  get isUsingMultipleCategories() {
    return this.enabledCategoryCount > 1;
  }

  #optionsCache = createCache(() => {
    // get categorized matches based on search query (if not disabled)
    const cityOptions = this.#cityOptions;
    const countryOptions = this.#countryOptions;
    const regionOptions = this.#regionOptions;

    const numberOfCategories = Object.keys(CATEGORIES).length;
    const numberOfOptions = cityOptions.length + countryOptions.length + regionOptions.length;

    // determine how many results will be shown
    const maxPossibleDistribution = Number.parseInt(this.args.categoryResultLimit) || MAX_RESULT_COUNT;

    let cityMatchLimit = 0;
    let countryMatchLimit = 0;
    let regionMatchLimit = 0;

    // if multiple categories are enabled we won't always have an even amount of matches.
    // still attempt to display the desired number of results when multiple categories present.
    for (let i = 0; i < numberOfOptions * numberOfCategories; i++) {
      // base case to stop distributing spots to the categories
      const spotsReserved = cityMatchLimit + countryMatchLimit + regionMatchLimit;
      if (spotsReserved === maxPossibleDistribution) {
        break;
      }

      // evenly distribute the available spots to each category
      // leftover spots go to the most precise category
      if (i % numberOfCategories === 0 && cityOptions.length > cityMatchLimit) {
        cityMatchLimit += 1;
      } else if (i % numberOfCategories === 1 && countryOptions.length > countryMatchLimit) {
        countryMatchLimit += 1;
      } else if (regionOptions.length > regionMatchLimit) {
        regionMatchLimit += 1;
      }
    }

    // return up to max desired results split up by category
    return {
      [CATEGORIES.CITY]: cityOptions.slice(0, cityMatchLimit),
      [CATEGORIES.COUNTRY]: countryOptions.slice(0, countryMatchLimit),
      [CATEGORIES.REGION]: regionOptions.slice(0, regionMatchLimit),
    };
  });

  get options() {
    return getValue(this.#optionsCache);
  }

  #optionsCountCache = createCache(() => {
    const { options } = this;
    return Object.keys(options).reduce((count, category) => {
      return count + options[category].length;
    }, 0);
  });

  get optionsCount() {
    return getValue(this.#optionsCountCache);
  }

  get noOptionsFound() {
    return this.value.length > 0
      && this.optionsCount === 0
      && !this.isLoadingOptions;
  }

  get inputDisabled() {
    return this.args.disabled || this.handleOptionClick.isRunning;
  }

  /**
   * Get city matches from Google API response
   */
  get #cityOptions() {
    return this.cities;
  }

  /**
   * Get country matches based on query
   */
  get #countryOptions() {
    if (!this.value) return [];

    const searchQuery = this.value.toLowerCase();
    return this.countries.filter(({ name }) => name.toLowerCase().includes(searchQuery));
  }

  /**
   * Get region matches based on query
   */
  get #regionOptions() {
    if (!this.value) return [];

    const searchQuery = this.value.toLowerCase();
    return this.regions.filter(({ name }) => name.toLowerCase().includes(searchQuery));
  }

  /**
   * Destination of the Google PlacesService DOM element
   */
  get destinationElement() {
    return document.body;
  }

  /**
   * Create a reference to the DOM element required to use the Google PlacesService
   *
   * @param {HTMLDivElement} element
   */
  @action
  setMapRef(element) {
    if (element) {
      // only fire up places service if ref is set
      this.googlePlacesService = new google.maps.places.PlacesService(element);
    } else if (this.googlePlacesService) {
      // destroy reference to services
      this.googlePlacesService = null;
    }
  }

  /**
   * Input field received input
   *
   * @param {InputEvent} e
   */
  @action
  handleInput(value) {
    this.isDirty = true;
    this.args.oninput?.(value);

    // feed subscription new value
    this.#valueSubscription?.next(value);
  }

  /**
   * Input gained focus
   */
  @action
  handleFocus() {
    this.hasFocus = true;
    this.isDirty = true;

    if (this.#initialized) return;

    // cache countries since there are not many
    if (!this.args.disableCountries) {
      this.loadCountries.perform();
    }

    // cache countries since there are not many
    if (!this.args.disableRegions) {
      this.loadRegions.perform();
    }

    // initialize google places api to fetch cities
    if (!this.args.disableCities) {
      this.loadCitiesAPI.perform();
    }

    this.#initialized = true;
  }

  /**
  * Input lost focus
  */
  @action
  handleBlur() {
    this.hasFocus = false;
    this.isDirty = false;
  }

  /**
   * Key pressed while input focused
   *
   * @param {KeyboardEvent} e
   */
  @action
  handleKeyDown(e) {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.isDirty = false;
        break;
    }
  }

  /**
   * Clear button clicked
   */
  @action
  handleClear() {
    // cancel tasks
    this.loadCities.cancelAll({ resetState: true });
    this.handleOptionClick.cancelAll({ resetState: true });
    this.loadCityLocation.cancelAll({ resetState: true });

    // reset state
    this.isDirty = true;
    this.args.oninput?.('');

    // feed subscription new value
    this.#valueSubscription?.next('');

    // remove selection
    this.args.onchange?.(null);
  }

  @dropTask
  *setInputSubscription() {
    if (this.args.disableCities) return;

    const subject = new BehaviorSubject(this.value);
    subject.pipe(
      debounceTime(SEARCH_DEBOUNCE_TIME_MS),
      distinctUntilChanged(),
    );

    yield waitForProperty(this, 'googleAutocompleteService');

    this.#valueSubscription = subject.subscribe({
      next: (search) => {
        this.loadCities.perform(search);
      },
    });
  }

  /**
   * Dropdown option clicked.
   * Region is cached. Country is cached. City must be resolved.
   */
  @dropTask
  *handleOptionClick(option, category) {
    this.isDirty = false;

    if (CATEGORIES.CITY === category) {
      // set the output to the raw api value until it resolves
      this.args.oninput?.(option.description);

      // resolve the location model
      try {
        const city = yield this.loadCityLocation.perform(option.place_id, this.#googleAutocompleteSessionToken);
        this.args.oninput?.(city.formattedAddress);

        // pass the Location model
        this.args.onchange?.(city);
      } catch (e) {
        this.args.onfail?.(e);
      } finally {
        // next session needs a unique token for optimal billing
        this.#googleAutocompleteSessionToken = null;
      }
    } else {
      this.args.oninput?.(option.name);

      // pass the Region or Country model
      this.args.onchange?.(option);
    }
  }

  /**
   * Fetch the location model from the google place_id
   */
  @task
  *loadCityLocation(placeId, sessionToken) {
    const place = yield new Promise((resolve, reject) => {
      const request = {
        ...GOOGLE_PLACE_DETAILS_OPTIONS,
        placeId,
        sessionToken,
      };

      this.googlePlacesService.getDetails(request, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          reject(new Error(`Could not find city ${place}`));
        } else {
          resolve(place);
        }
      });
    });

    return this.locationCommandService.createLocationFromGooglePlace(place);
  }

  /**
   * Load city suggestions from Google Places API based on the input
   *
   * @param {String} input
   * @returns {Array}
   * @throws Error if there was a Google API server error
   */
  @task
  *loadCities(input) {
    if (!input) return;

    // hasn't initialized
    if (!this.googleAutocompleteService) return;

    // create a session token for billing optimization
    this.#googleAutocompleteSessionToken ??= new google.maps.places.AutocompleteSessionToken();

    // uses session tokens to group together autocomplete requests for billing purposes
    const result = yield this.googleAutocompleteService.getPlacePredictions({
        ...GOOGLE_PLACE_AUTOCOMPLETE_OPTIONS,
        input,
        sessionToken: this.#googleAutocompleteSessionToken,
      }, handleAPIResponse);

    // update if API gave us results
    this.cities = Object.values(result.predictions).reduce((accum, { place_id, description }) => {
      accum.push({
        place_id,
        description,
      });
      return accum;
    }, []);
  }

  /**
   * Load Google Places library
   */
  @dropTask
  *loadCitiesAPI() {
    try {
      // wait for google places to load before creating stream
      yield this.googlePlaces.load();
    } catch {
      // error is already logged in the `services/google-places`
      return;
    }

    this.googleAutocompleteService = new google.maps.places.AutocompleteService();
  }

  /**
   * Fetch and cache all countries for later filtering
   *
   * @returns {Array<Country>}
   */
  @dropTask
  *loadCountries() {
    if (!this.countries.length) {
      this.countries = yield this.store.findAll('country', {
        adapterOptions: { isPublic: true },
      });
    }

    return this.countries;
  }

  /**
   * Fetch and cache all regions for later filtering
   *
   * @returns {Array<Region>}
   */
  @dropTask
  *loadRegions() {
    if (!this.regions.length) {
      this.regions = yield this.store.findAll('region', {
        adapterOptions: { isPublic: true },
      });
    }

    return this.regions;
  }
}

export default SignupLocationInput;
